import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, Check, ChevronRight, Zap, Circle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import axios from "axios";
import { API } from "@/lib/api";

export function OnboardingChecklist({ onDismiss }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/onboarding/status`);
      setStatus(res.data);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const completeItem = async (itemId) => {
    try {
      await axios.post(`${API}/onboarding/complete-item`, { item_id: itemId });
      setStatus(prev => ({
        ...prev,
        completed_items: [...(prev?.completed_items || []), itemId],
      }));
    } catch { /* ignore */ }
  };

  const handleDismiss = async () => {
    try {
      await axios.post(`${API}/onboarding/dismiss`);
    } catch { /* ignore */ }
    onDismiss?.();
  };

  if (loading || !status || status.dismissed || status.checklist_completed) return null;

  const completedSet = new Set(status.completed_items || []);
  const total = status.checklist?.length || 5;
  const done = completedSet.size;
  const pct = Math.round((done / total) * 100);

  return (
    <div className="mx-3 mb-3 rounded-lg border border-green-500/20 bg-green-500/5 overflow-hidden" data-testid="onboarding-checklist">
      <div className="flex items-center justify-between px-3 pt-3 pb-1">
        <div className="flex items-center gap-2">
          <Zap className="w-3.5 h-3.5 text-green-500" />
          <span className="text-xs font-semibold text-green-400">Getting Started</span>
        </div>
        <button
          onClick={handleDismiss}
          className="text-zinc-600 hover:text-zinc-400 transition-colors"
          data-testid="dismiss-checklist-btn"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Progress bar */}
      <div className="px-3 py-1.5">
        <div className="flex items-center justify-between text-xs text-zinc-500 mb-1">
          <span>{done}/{total} completed</span>
          <span>{pct}%</span>
        </div>
        <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
          <motion.div
            className="h-full rounded-full bg-green-500"
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.4 }}
          />
        </div>
      </div>

      {/* Checklist items */}
      <div className="px-2 pb-2 space-y-0.5">
        {(status.checklist || []).map((item) => {
          const isDone = completedSet.has(item.id);
          return (
            <Link
              key={item.id}
              to={item.link}
              onClick={() => { if (!isDone) completeItem(item.id); }}
              className={cn(
                "flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors group",
                isDone
                  ? "text-zinc-600"
                  : "text-zinc-400 hover:text-white hover:bg-zinc-800/50"
              )}
              data-testid={`checklist-item-${item.id}`}
            >
              {isDone ? (
                <Check className="w-3.5 h-3.5 text-green-500 shrink-0" />
              ) : (
                <Circle className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
              )}
              <span className={cn("flex-1", isDone && "line-through")}>{item.label}</span>
              {!isDone && <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export function WelcomeModal({ onClose }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const check = async () => {
      try {
        const res = await axios.get(`${API}/onboarding/status`);
        if (res.data?.show_welcome) {
          setVisible(true);
        }
      } catch { /* not logged in or error */ }
    };
    check();
  }, []);

  const handleClose = async () => {
    setVisible(false);
    try {
      await axios.post(`${API}/onboarding/welcome-seen`);
    } catch { /* ignore */ }
    onClose?.();
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
          onClick={handleClose}
          data-testid="welcome-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-md w-full p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="welcome-modal"
          >
            <div className="text-center">
              <div className="w-12 h-12 rounded-xl bg-green-500/10 border border-green-500/20 flex items-center justify-center mx-auto mb-4">
                <Zap className="w-6 h-6 text-green-500" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Welcome to SEO Toolkit</h2>
              <p className="text-sm text-zinc-400 mb-6 leading-relaxed">
                You're all set. We've put together a quick checklist in the sidebar to help you get the most out of the platform. You can also explore the full overview on the Getting Started page.
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <Button asChild className="bg-green-600 hover:bg-green-700 w-full" data-testid="welcome-get-started-btn">
                <Link to="/getting-started" onClick={handleClose}>
                  <Zap className="w-4 h-4 mr-2" />
                  Getting Started Guide
                </Link>
              </Button>
              <Button
                variant="ghost"
                className="text-zinc-500 hover:text-zinc-300 w-full"
                onClick={handleClose}
                data-testid="welcome-dismiss-btn"
              >
                I'll explore on my own
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
